import { WidgetUploadError } from './upload-thread-file.js';

export const MAX_IMAGE_UPLOAD_BYTES = 10 * 1024 * 1024;

/** Per decode/reencode attempt (native / HTML image / WASM). */
export const IMAGE_CONVERT_ATTEMPT_TIMEOUT_MS = 10_000;

const ACCEPTED_IMAGE_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/heic',
  'image/heif',
]);

const EXT_BY_MIME: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/heic': 'heic',
  'image/heif': 'heif',
};

const MIME_BY_EXT: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  heic: 'image/heic',
  heif: 'image/heif',
};

const COMPRESS_STEPS: Array<{ maxEdge: number; quality: number }> = [
  { maxEdge: 2048, quality: 0.85 },
  { maxEdge: 1600, quality: 0.75 },
  { maxEdge: 1280, quality: 0.65 },
  { maxEdge: 1024, quality: 0.55 },
];

function fileExtension(name: string): string | null {
  const match = /\.([a-z0-9]+)$/i.exec(name.trim());
  return match ? match[1]!.toLowerCase() : null;
}

/** Normalize MIME aliases and infer from filename when needed. */
export function resolveOutboundImageMime(file: File): string {
  const raw = (file.type || '').trim().toLowerCase();
  if (raw === 'image/jpg') return 'image/jpeg';
  if (raw && ACCEPTED_IMAGE_TYPES.has(raw)) return raw;

  const ext = fileExtension(file.name);
  if (ext && MIME_BY_EXT[ext]) return MIME_BY_EXT[ext]!;

  return raw;
}

export function ensureOutboundImageFileName(
  name: string,
  mime: string,
): string {
  const trimmed = name.trim();
  const ext = EXT_BY_MIME[mime] ?? 'jpg';
  if (!trimmed || trimmed === 'image' || trimmed === 'blob') {
    return `image.${ext}`;
  }
  if (/\.(jpe?g|png|gif|webp|heic|heif)$/i.test(trimmed)) {
    if (mime === 'image/jpeg' && /\.jpe?g$/i.test(trimmed)) return trimmed;
    if (mime !== 'image/jpeg' && new RegExp(`\\.${ext}$`, 'i').test(trimmed)) {
      return trimmed;
    }
    if (mime === 'image/jpeg') {
      return trimmed.replace(/\.[a-z0-9]+$/i, '.jpg');
    }
  }
  return `${trimmed.replace(/\.[a-z0-9]+$/i, '')}.${ext}`;
}

export function isHeicLike(mime: string, fileName: string): boolean {
  return (
    mime === 'image/heic' ||
    mime === 'image/heif' ||
    /\.heic$/i.test(fileName) ||
    /\.heif$/i.test(fileName)
  );
}

function needsReencode(mime: string, fileName: string): boolean {
  return (
    isHeicLike(mime, fileName) ||
    !mime ||
    mime === 'application/octet-stream'
  );
}

function isLikelyImage(file: File, mime: string): boolean {
  if (ACCEPTED_IMAGE_TYPES.has(mime)) return true;
  if (/\.(jpe?g|png|gif|webp|heic|heif)$/i.test(file.name)) return true;
  return !file.type || file.type === 'application/octet-stream';
}

function withMeta(blob: Blob, fileName: string, mime: string): File {
  return new File([blob], fileName, { type: mime, lastModified: Date.now() });
}

async function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  label: string,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timer = setTimeout(() => {
          reject(new Error(`[Teo Widget] ${label} timed out after ${ms}ms`));
        }, ms);
      }),
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

async function canvasToJpegBlob(
  source: CanvasImageSource,
  width: number,
  height: number,
  maxEdge: number,
  quality: number,
): Promise<Blob | null> {
  const scale = Math.min(1, maxEdge / Math.max(width, height));
  const targetW = Math.max(1, Math.round(width * scale));
  const targetH = Math.max(1, Math.round(height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.drawImage(source, 0, 0, targetW, targetH);

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality);
  });
}

function jpegFileFromBlob(file: File, blob: Blob): File {
  const name = ensureOutboundImageFileName(
    file.name.replace(/\.[a-z0-9]+$/i, '') || 'image',
    'image/jpeg',
  );
  return withMeta(blob, name, 'image/jpeg');
}

async function reencodeToJpeg(
  file: File,
  maxEdge: number,
  quality: number,
): Promise<File | null> {
  if (typeof createImageBitmap !== 'function') return null;

  let bitmap: ImageBitmap | null = null;
  try {
    bitmap = await withTimeout(
      createImageBitmap(file),
      IMAGE_CONVERT_ATTEMPT_TIMEOUT_MS,
      'createImageBitmap',
    );
    const blob = await withTimeout(
      canvasToJpegBlob(
        bitmap,
        bitmap.width,
        bitmap.height,
        maxEdge,
        quality,
      ),
      IMAGE_CONVERT_ATTEMPT_TIMEOUT_MS,
      'canvas.toBlob',
    );
    if (!blob) return null;
    return jpegFileFromBlob(file, blob);
  } catch {
    return null;
  } finally {
    bitmap?.close();
  }
}

/**
 * iOS Safari/WebKit often decodes Photos HEIC in `<img>` when
 * `createImageBitmap` fails or hangs.
 */
export async function reencodeViaHtmlImage(
  file: File,
  maxEdge: number,
  quality: number,
): Promise<File | null> {
  if (typeof document === 'undefined' || typeof Image === 'undefined') {
    return null;
  }

  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await withTimeout(
      new Promise<HTMLImageElement>((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = () => reject(new Error('HTMLImageElement decode failed'));
        el.src = objectUrl;
      }),
      IMAGE_CONVERT_ATTEMPT_TIMEOUT_MS,
      'HTMLImageElement decode',
    );

    const width = img.naturalWidth || img.width;
    const height = img.naturalHeight || img.height;
    if (!width || !height) return null;

    const blob = await withTimeout(
      canvasToJpegBlob(img, width, height, maxEdge, quality),
      IMAGE_CONVERT_ATTEMPT_TIMEOUT_MS,
      'canvas.toBlob',
    );
    if (!blob || blob.size === 0) return null;
    return jpegFileFromBlob(file, blob);
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/**
 * Lazy WASM decode (Chrome / Firefox). Uses `heic-to/csp` so hosts with strict
 * CSP do not need `wasm-unsafe-eval` for the default build path.
 */
export async function convertHeicWithWasm(
  file: File,
  quality = 0.85,
): Promise<File | null> {
  try {
    const { heicTo } = await import('heic-to/csp');
    const blob = await withTimeout(
      heicTo({
        blob: file,
        type: 'image/jpeg',
        quality,
      }),
      IMAGE_CONVERT_ATTEMPT_TIMEOUT_MS,
      'heic-to WASM',
    );
    if (!blob || blob.size === 0) return null;
    return jpegFileFromBlob(file, blob);
  } catch {
    return null;
  }
}

async function compressUnderLimit(file: File): Promise<File | null> {
  for (const step of COMPRESS_STEPS) {
    let next = await reencodeToJpeg(file, step.maxEdge, step.quality);
    if (!next) {
      next = await reencodeViaHtmlImage(file, step.maxEdge, step.quality);
    }
    if (next && next.size <= MAX_IMAGE_UPLOAD_BYTES) return next;
  }
  return null;
}

/**
 * Prepare a picker/camera `File` for `POST …/upload-file`:
 * fix MIME/name quirks, convert HEIC to JPEG (native → HTML image → WASM),
 * and compress oversized photos under 10MB. Never uploads undecoded HEIC.
 */
export async function normalizeOutboundImageFile(file: File): Promise<File> {
  const mime = resolveOutboundImageMime(file);

  if (!isLikelyImage(file, mime)) {
    throw new WidgetUploadError(
      'UNSUPPORTED_FORMAT',
      '[Teo Widget] Only image files are supported',
    );
  }

  let out: File;

  if (needsReencode(mime, file.name)) {
    let jpeg = await reencodeToJpeg(file, 2048, 0.85);
    if (!jpeg) {
      jpeg = await reencodeViaHtmlImage(file, 2048, 0.85);
    }
    if (!jpeg && isHeicLike(mime, file.name)) {
      jpeg = await convertHeicWithWasm(file, 0.85);
    }

    if (jpeg) {
      out = jpeg;
    } else if (isHeicLike(mime, file.name)) {
      throw new WidgetUploadError(
        'UNSUPPORTED_FORMAT',
        '[Teo Widget] Could not convert HEIC/HEIF to JPEG',
      );
    } else {
      throw new WidgetUploadError(
        'UNSUPPORTED_FORMAT',
        '[Teo Widget] Image format is not supported',
      );
    }
  } else {
    const name = ensureOutboundImageFileName(file.name, mime);
    out =
      name === file.name && file.type === mime
        ? file
        : withMeta(file, name, mime);
  }

  if (out.size > MAX_IMAGE_UPLOAD_BYTES) {
    const compressed = await compressUnderLimit(out);
    if (!compressed) {
      throw new WidgetUploadError(
        'TOO_LARGE',
        '[Teo Widget] Image exceeds size limit',
      );
    }
    out = compressed;
  }

  return out;
}
